import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BookOpen, Award, CheckCircle, Sparkles, TrendingUp, Shield, Zap } from 'lucide-react';
export function PeptideMasteryDetailSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-4002">
      {/* Mystical background effects - matching existing sections */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-4003" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-purple-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" data-id="element-4004" />
      <div className="absolute bottom-1/4 right-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-blue-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-4005" />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto" data-id="element-4006">
        {/* Main Headline */}
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-psi-pro-details-section data-id="element-4007">
          Here's Exactly What You're Getting When You Join{' '}
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-4008">
            PSI PRO
          </span>
          :
        </h2>

        <p className="text-lg sm:text-xl md:text-2xl text-stone-300 text-center mb-10 sm:mb-16" data-id="element-4009">
          Let me break down the{' '}
          <span className="text-amber-400 font-bold" data-id="element-4010">COMPLETE</span> package...
        </p>

        {/* PEPTIDE MASTERY 2.0 - MAIN SECTION */}
        <div className="relative mb-12 sm:mb-20" data-id="element-4011">
          <div className="absolute inset-0 bg-gradient-radial from-amber-600/20 via-transparent to-transparent blur-3xl" data-id="element-4012" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-10 md:p-12 rounded-lg shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-4013">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-4014">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-4015" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-4016" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-4017">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-4018" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-4019" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-4020">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-4021" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-4022" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-4023">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-4024" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-4025" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-6 sm:mb-8" data-id="element-4026">
              <div className="relative" data-id="element-4027">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-4028" />
                <CheckCircle className="relative w-12 h-12 sm:w-16 sm:h-16 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-4029" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6 sm:mb-8" data-id="element-4030">
              <h3 className="text-2xl sm:text-4xl md:text-5xl font-serif font-bold text-amber-400 mb-4" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
            }} data-id="element-4031">
                PEPTIDE MASTERY 2.0 — THE COMPLETE BIOLOGICAL OPTIMIZATION
                BLUEPRINT
              </h3>

              <div className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-green-600/30 border-2 border-green-500/50 rounded-lg mt-4" data-id="element-4032">
                <p className="text-green-400 font-bold text-lg sm:text-xl" data-id="element-4033">
                  Included FREE when you join PSI PRO.
                </p>
              </div>

              <p className="text-stone-400 text-base sm:text-lg mt-4" data-id="element-4034">
                (Normally $2,500 — pay-in-full only, no payment plans)
              </p>
            </div>

            {/* Description */}
            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 mb-8 sm:mb-12" data-id="element-4035">
              <p className="text-lg sm:text-xl text-stone-100 font-semibold text-center" data-id="element-4036">
                This is the most comprehensive, scientifically validated,
                in-depth peptide training ever created.
              </p>

              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-4 sm:p-6 space-y-3" data-id="element-4037">
                <p className="text-stone-100" data-id="element-4038">
                  The same knowledge base used when{' '}
                  <span className="font-bold text-amber-400" data-id="element-4039">
                    $120,000/year private clients
                  </span>{' '}
                  come to Brenden with "impossible" cases.
                </p>
                <p className="text-stone-100" data-id="element-4040">
                  The same protocols implemented when{' '}
                  <span className="font-bold text-amber-400" data-id="element-4041">
                    Olympic athletes
                  </span>{' '}
                  need an edge.
                </p>
                <p className="text-stone-100" data-id="element-4042">
                  The same frameworks applied when{' '}
                  <span className="font-bold text-amber-400" data-id="element-4043">
                    doctors send him their toughest cases
                  </span>
                  .
                </p>
              </div>

              <p className="text-xl sm:text-2xl font-bold text-center text-amber-400 mt-6 sm:mt-8" data-id="element-4044">
                Here's what's inside:
              </p>
            </div>

            {/* 130+ PEPTIDES SECTION */}
            <div className="mb-10 sm:mb-16" data-id="element-4045">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 sm:p-8 rounded-lg" data-id="element-4046">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-4047">
                  <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" data-id="element-4048" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-purple-400" data-id="element-4049">
                    130+ PEPTIDES DECODED AT THE MOLECULAR LEVEL
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-4050">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-4051">
                    Complete modules on over 130 powerful peptides — including
                    rare ones most "experts" have never heard of (and never will
                    unless they go to the source like Brenden has).
                  </p>

                  <p className="text-amber-400 font-bold" data-id="element-4052">
                    This is the ONLY course in the world that truly covers all
                    of Khavinson's peptides.
                  </p>

                  <p className="text-stone-100 font-semibold" data-id="element-4053">
                    Not summaries. Not overviews.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-4054">
                    Deep mechanistic breakdowns of:
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-purple-500/30 text-sm sm:text-base" data-id="element-4055">
                    <p className="flex items-start gap-3" data-id="element-4056">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-4057" />
                      <span data-id="element-4058">
                        <span className="font-bold text-purple-400" data-id="element-4059">
                          Exact mechanisms of action
                        </span>{' '}
                        (what it actually does in your body at the cellular and
                        molecular level)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-4060">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-4061" />
                      <span data-id="element-4062">
                        <span className="font-bold text-purple-400" data-id="element-4063">
                          Optimal dosing protocols
                        </span>{' '}
                        (the doses that actually work, based on real research
                        and real results)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-4064">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-4065" />
                      <span data-id="element-4066">
                        <span className="font-bold text-purple-400" data-id="element-4067">
                          Synergistic combinations
                        </span>{' '}
                        (how to stack peptides for exponential rather than
                        additive effects)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-4068">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-4069" />
                      <span data-id="element-4070">
                        <span className="font-bold text-purple-400" data-id="element-4071">
                          Timing and absorption optimization
                        </span>{' '}
                        (when to dose for maximum bioavailability)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-4072">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-4073" />
                      <span data-id="element-4074">
                        <span className="font-bold text-purple-400" data-id="element-4075">
                          Safety considerations
                        </span>{' '}
                        (real risks based on actual science, not internet
                        fear-mongering)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-4076">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-4077" />
                      <span data-id="element-4078">
                        <span className="font-bold text-purple-400" data-id="element-4079">
                          Product type identification
                        </span>{' '}
                        (how to tell if you're buying Type 1, 2, or 3
                        bioregulators)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-4080">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-4081" />
                      <span data-id="element-4082">
                        <span className="font-bold text-purple-400" data-id="element-4083">
                          Where to source high-quality versions
                        </span>{' '}
                        (because knowing what to take is worthless if what
                        you're taking is bunk)
                      </span>
                    </p>
                  </div>

                  <div className="bg-purple-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-4084">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-4085">
                      This goes far beyond what you'll find in any other course,
                      book, or training program.
                    </p>
                    <p className="text-purple-400 font-semibold mt-2 text-sm sm:text-base" data-id="element-4086">
                      Because no other program was created by someone who's
                      actually gone to the source.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 54 PROTOCOLS SECTION */}
            <div className="mb-10 sm:mb-16" data-id="element-4087">
              <div className="bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-6 sm:p-8 rounded-lg" data-id="element-4088">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-4089">
                  <Zap className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-400" data-id="element-4090" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-cyan-400" data-id="element-4091">
                    54 BRENDEN HENRY PROTOCOLS
                  </h4>
                </div>

                <p className="text-xl sm:text-2xl text-cyan-300 font-bold mb-6" data-id="element-4092">
                  Precision-Engineered Synergistic Protocols That Create
                  "Impossible" Transformations
                </p>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-4093">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-4094">
                    This is where theory becomes transformation.
                  </p>

                  <p data-id="element-4095">
                    These aren't generic templates you could find with enough
                    Google searching.
                  </p>
                  <p data-id="element-4096">These aren't "here's what might work" suggestions.</p>

                  <p className="text-lg sm:text-xl text-cyan-400 font-bold" data-id="element-4097">
                    These are precision-engineered protocols Brenden has
                    developed through:
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-cyan-500/30 text-sm sm:text-base" data-id="element-4098">
                    <p className="flex items-start gap-3" data-id="element-4099">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-4100">→</span>
                      <span data-id="element-4101">
                        Years of translating original Russian research
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-4102">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-4103">→</span>
                      <span data-id="element-4104">
                        Extensive experimentation with 1,500+ participants
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-4105">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-4106">→</span>
                      <span data-id="element-4107">
                        Real-world application with private $15,000−$120,000
                        transformation clients
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-4108">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-4109">→</span>
                      <span data-id="element-4110">
                        Deep mechanistic understanding of peptide synergies
                        nobody else knows
                      </span>
                    </p>
                  </div>

                  <div className="bg-cyan-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-4111">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-4112">
                      Each protocol shows you:
                    </p>
                    <div className="space-y-2 pl-4 text-sm sm:text-base" data-id="element-4113">
                      <p className="flex items-start gap-3" data-id="element-4114">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-4115" />
                        <span data-id="element-4116">Exactly what peptides to use</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-4117">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-4118" />
                        <span data-id="element-4119">Precise dosing schedules</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-4120">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-4121" />
                        <span data-id="element-4122">How to stack them for maximum synergy</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-4123">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-4124" />
                        <span data-id="element-4125">Expected timeline for results</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-4126">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-4127" />
                        <span data-id="element-4128">How to source everything you need</span>
                      </p>
                    </div>
                  </div>

                  {/* Protocol Categories */}
                  <div className="mt-8" data-id="element-4129">
                    <p className="text-xl sm:text-2xl font-bold text-cyan-400 mb-6 text-center" data-id="element-4130">
                      Here are all 54 protocols you're getting:
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-4131">
                      {/* REGENERATION & HEALING */}
                      <div className="bg-emerald-950/30 border border-emerald-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4132">
                        <h5 className="text-lg sm:text-xl font-bold text-emerald-400 mb-4" data-id="element-4133">
                          REGENERATION & HEALING:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4134">
                          <li data-id="element-4135">• Liver Regeneration</li>
                          <li data-id="element-4136">• Kidney Repair</li>
                          <li data-id="element-4137">• Bone Healing</li>
                          <li data-id="element-4138">• Accelerated Healing</li>
                          <li data-id="element-4139">• Regenerate Spinal Discs</li>
                        </ul>
                      </div>

                      {/* NEUROLOGICAL & COGNITIVE */}
                      <div className="bg-purple-950/30 border border-purple-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4140">
                        <h5 className="text-lg sm:text-xl font-bold text-purple-400 mb-4" data-id="element-4141">
                          NEUROLOGICAL & COGNITIVE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4142">
                          <li data-id="element-4143">• Traumatic Brain Injury Recovery</li>
                          <li data-id="element-4144">• Stroke Recovery</li>
                          <li data-id="element-4145">• Alzheimer's Disease Regeneration</li>
                          <li data-id="element-4146">• Parkinson's Disease Regeneration</li>
                          <li data-id="element-4147">• Cognitive Decline / Brain Fog Solution</li>
                          <li data-id="element-4148">
                            • Improve Speed of Learning and Memory Retention
                          </li>
                          <li data-id="element-4149">• ADHD Calm Focus Protocol</li>
                        </ul>
                      </div>

                      {/* MENTAL HEALTH & NEUROCHEMISTRY */}
                      <div className="bg-blue-950/30 border border-blue-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4150">
                        <h5 className="text-lg sm:text-xl font-bold text-blue-400 mb-4" data-id="element-4151">
                          MENTAL HEALTH & NEUROCHEMISTRY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4152">
                          <li data-id="element-4153">• Anti-Anxiety</li>
                          <li data-id="element-4154">• Mood Enhancement and Depression Relief</li>
                          <li data-id="element-4155">• Anhedonia Fix & Pleasure Re-Sensitization</li>
                          <li data-id="element-4156">• Addiction Support Stack</li>
                        </ul>
                      </div>

                      {/* METABOLIC & ENDOCRINE */}
                      <div className="bg-amber-950/30 border border-amber-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4157">
                        <h5 className="text-lg sm:text-xl font-bold text-amber-400 mb-4" data-id="element-4158">
                          METABOLIC & ENDOCRINE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4159">
                          <li data-id="element-4160">• Diabetes Control</li>
                          <li data-id="element-4161">• Fix Hypothyroidism</li>
                          <li data-id="element-4162">• Male Hormone Optimization</li>
                          <li data-id="element-4163">• Female Hormone Optimization</li>
                          <li data-id="element-4164">• Low Energy and Chronic Fatigue Fix</li>
                          <li data-id="element-4165">
                            • Stubborn Fat Loss and Muscle Gain Enhancement
                          </li>
                        </ul>
                      </div>

                      {/* CARDIOVASCULAR & CIRCULATORY */}
                      <div className="bg-red-950/30 border border-red-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4166">
                        <h5 className="text-lg sm:text-xl font-bold text-red-400 mb-4" data-id="element-4167">
                          CARDIOVASCULAR & CIRCULATORY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4168">
                          <li data-id="element-4169">• Ultimate Heart Protocol</li>
                          <li data-id="element-4170">• Blood Vessel and Artery Support</li>
                          <li data-id="element-4171">• Anemia Fix</li>
                        </ul>
                      </div>

                      {/* IMMUNE & AUTOIMMUNE */}
                      <div className="bg-green-950/30 border border-green-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4172">
                        <h5 className="text-lg sm:text-xl font-bold text-green-400 mb-4" data-id="element-4173">
                          IMMUNE & AUTOIMMUNE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4174">
                          <li data-id="element-4175">• Immune System Fortification</li>
                          <li data-id="element-4176">• Autoimmune Support</li>
                          <li data-id="element-4177">• Cancer Recovery Support</li>
                        </ul>
                      </div>

                      {/* ANTI-AGING & LONGEVITY */}
                      <div className="bg-pink-950/30 border border-pink-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4178">
                        <h5 className="text-lg sm:text-xl font-bold text-pink-400 mb-4" data-id="element-4179">
                          ANTI-AGING & LONGEVITY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4180">
                          <li data-id="element-4181">• Biological Age Reversal</li>
                          <li data-id="element-4182">• Elderly Lifespan Stack</li>
                          <li data-id="element-4183">• Skin Rejuvenation</li>
                          <li data-id="element-4184">• Hair Growth and Health</li>
                        </ul>
                      </div>

                      {/* RESPIRATORY & ENVIRONMENTAL */}
                      <div className="bg-teal-950/30 border border-teal-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4185">
                        <h5 className="text-lg sm:text-xl font-bold text-teal-400 mb-4" data-id="element-4186">
                          RESPIRATORY & ENVIRONMENTAL:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4187">
                          <li data-id="element-4188">• Lung Power</li>
                          <li data-id="element-4189">• Ultimate Cold Tolerance</li>
                          <li data-id="element-4190">• Ultimate Anti-Hypoxiant</li>
                          <li data-id="element-4191">• "Hold Your Breath for Longer" Protocol</li>
                          <li data-id="element-4192">• Radiation Resilience and Recovery Regimen</li>
                        </ul>
                      </div>

                      {/* PERFORMANCE & ATHLETICS */}
                      <div className="bg-orange-950/30 border border-orange-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4193">
                        <h5 className="text-lg sm:text-xl font-bold text-orange-400 mb-4" data-id="element-4194">
                          PERFORMANCE & ATHLETICS:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4195">
                          <li data-id="element-4196">• Athletic Performance Enhancement</li>
                          <li data-id="element-4197">• Military Personnel Protocol</li>
                        </ul>
                      </div>

                      {/* PAIN & INFLAMMATION */}
                      <div className="bg-rose-950/30 border border-rose-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4198">
                        <h5 className="text-lg sm:text-xl font-bold text-rose-400 mb-4" data-id="element-4199">
                          PAIN & INFLAMMATION:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4200">
                          <li data-id="element-4201">• Chronic Pain Management</li>
                          <li data-id="element-4202">• Neuropathy Support</li>
                          <li data-id="element-4203">• Chronic Prostatitis Cure</li>
                        </ul>
                      </div>

                      {/* SENSORY & RESTORATION */}
                      <div className="bg-indigo-950/30 border border-indigo-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4204">
                        <h5 className="text-lg sm:text-xl font-bold text-indigo-400 mb-4" data-id="element-4205">
                          SENSORY & RESTORATION:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4206">
                          <li data-id="element-4207">• Eye Health and Vision Enhancement</li>
                          <li data-id="element-4208">• Reverse Hearing Loss</li>
                          <li data-id="element-4209">• Restore Olfaction</li>
                        </ul>
                      </div>

                      {/* DIGESTIVE & ORAL */}
                      <div className="bg-lime-950/30 border border-lime-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4210">
                        <h5 className="text-lg sm:text-xl font-bold text-lime-400 mb-4" data-id="element-4211">
                          DIGESTIVE & ORAL:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4212">
                          <li data-id="element-4213">• Digestive Health</li>
                          <li data-id="element-4214">• Reverse Gum Disease and Periodontitis</li>
                        </ul>
                      </div>

                      {/* SLEEP & RECOVERY */}
                      <div className="bg-violet-950/30 border border-violet-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4215">
                        <h5 className="text-lg sm:text-xl font-bold text-violet-400 mb-4" data-id="element-4216">
                          SLEEP & RECOVERY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4217">
                          <li data-id="element-4218">• Sleep Optimization</li>
                        </ul>
                      </div>

                      {/* QUALITY OF LIFE */}
                      <div className="bg-fuchsia-950/30 border border-fuchsia-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4219">
                        <h5 className="text-lg sm:text-xl font-bold text-fuchsia-400 mb-4" data-id="element-4220">
                          QUALITY OF LIFE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4221">
                          <li data-id="element-4222">• Supercharged Libido Stack</li>
                          <li data-id="element-4223">• Enhanced Tanning Protocol</li>
                          <li data-id="element-4224">• Pregnancy Symptom Relief</li>
                        </ul>
                      </div>

                      {/* RARE CONDITIONS */}
                      <div className="bg-sky-950/30 border border-sky-500/30 p-4 sm:p-6 rounded-lg" data-id="element-4225">
                        <h5 className="text-lg sm:text-xl font-bold text-sky-400 mb-4" data-id="element-4226">
                          RARE CONDITIONS:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-4227">
                          <li data-id="element-4228">• Huntington's Disease Recovery</li>
                          <li data-id="element-4229">
                            • Duchenne Muscular Dystrophy or Spinal-Bulbar
                            Muscular Atrophy Healing
                          </li>
                          <li data-id="element-4230">• Sarcopenia Recovery</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-cyan-950/30 p-4 sm:p-6 rounded-lg mt-8" data-id="element-4231">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-4232">
                      Each one is a complete roadmap.
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-4233">
                      Each one is based on years of research and real human
                      results.
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-4234">
                      Each one gives you exactly what you need to implement it
                      successfully.
                    </p>
                    <p className="text-xl sm:text-2xl text-cyan-400 font-bold mt-6" data-id="element-4235">
                      Having ONE protocol that actually works for YOUR specific
                      situation is worth more than the entire investment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 1,193 SCIENTIFIC CITATIONS */}
            <div className="mb-10 sm:mb-16" data-id="element-4236">
              <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-6 sm:p-8 rounded-lg" data-id="element-4237">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-4238">
                  <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400" data-id="element-4239" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-blue-400" data-id="element-4240">
                    BACKED WITH 1,193 SCIENTIFIC CITATIONS
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-4241">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-4242">
                    Every claim is backed by peer-reviewed research.
                  </p>

                  <p data-id="element-4243">All available in the Master Citation List.</p>

                  <p className="text-blue-400 font-bold" data-id="element-4244">
                    We sell this citation list separately for $1,193 (one dollar
                    per citation).
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-4245">
                    But it's included inside Peptide Mastery for free.
                  </p>

                  <div className="bg-blue-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-4246">
                    <p className="text-stone-100 mb-2" data-id="element-4247">
                      This isn't bro-science.
                    </p>
                    <p className="text-stone-100 mb-2" data-id="element-4248">
                      This isn't anecdotal evidence.
                    </p>
                    <p className="text-stone-100 mb-4" data-id="element-4249">
                      This isn't "I read on Reddit that..."
                    </p>
                    <p className="text-lg sm:text-xl text-blue-400 font-bold" data-id="element-4250">
                      This is decades of scientific validation, centralized and
                      simplified into the most valuable biological enhancement
                      resource in the world.
                    </p>
                  </div>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-blue-500/30 mt-6 text-sm sm:text-base" data-id="element-4251">
                    <p data-id="element-4252">
                      When Brenden makes a claim, it's backed by original
                      research.
                    </p>
                    <p data-id="element-4253">
                      When he recommends a protocol, it's validated by studies
                      most people don't even know exist.
                    </p>
                    <p data-id="element-4254">
                      When he states a mechanism, it's supported by
                      peer-reviewed science.
                    </p>
                  </div>

                  <p className="text-xl sm:text-2xl text-center text-stone-100 font-bold mt-6" data-id="element-4255">
                    You're not getting opinions.
                  </p>
                  <p className="text-xl sm:text-2xl text-center text-blue-400 font-bold" data-id="element-4256">
                    You're getting facts.
                  </p>
                </div>
              </div>
            </div>

            {/* MASS SPECTROMETRY VERIFICATION */}
            <div className="mb-10 sm:mb-16" data-id="element-4257">
              <div className="bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-6 sm:p-8 rounded-lg" data-id="element-4258">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-4259">
                  <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400" data-id="element-4260" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-emerald-400" data-id="element-4261">
                    MASS SPECTROMETRY VERIFICATION
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-4262">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-4263">
                    We don't just tell you what to take.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-4264">
                    We show you PROOF that what you're buying is actually what
                    the label claims.
                  </p>

                  <div className="bg-red-950/30 border-l-4 border-red-500 p-4 sm:p-6 my-6" data-id="element-4265">
                    <p className="text-lg sm:text-xl text-red-400 font-bold mb-4" data-id="element-4266">
                      Because here's a dirty secret of the peptide industry:
                    </p>
                    <div className="space-y-2 text-stone-300 text-sm sm:text-base" data-id="element-4267">
                      <p data-id="element-4268">Not all peptides are created equal.</p>
                      <p data-id="element-4269">Some vendors are selling underdosed products.</p>
                      <p data-id="element-4270">Some are selling completely bunk products.</p>
                      <p data-id="element-4271">Some are selling contaminated products.</p>
                    </div>
                  </div>

                  <p className="text-stone-100 font-semibold" data-id="element-4272">
                    And you'd never know unless you had access to mass
                    spectrometry testing.
                  </p>

                  <p className="text-lg sm:text-xl text-emerald-400 font-bold" data-id="element-4273">
                    Which you now do.
                  </p>

                  <div className="bg-emerald-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-4274">
                    <p className="text-stone-100 mb-2 text-sm sm:text-base" data-id="element-4275">
                      <span className="font-bold text-emerald-400" data-id="element-4276">
                        Included in the course:
                      </span>{' '}
                      Mass spec analysis of popular peptide products, so you can
                      see exactly what's real and what's fake.
                    </p>
                    <p className="text-lg sm:text-xl text-emerald-400 font-bold mt-4" data-id="element-4277">
                      This alone has saved our clients thousands of dollars in
                      wasted purchases.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* LIFETIME UPDATES */}
            <div className="mb-10 sm:mb-16" data-id="element-4278">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 sm:p-8 rounded-lg" data-id="element-4279">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-4280">
                  <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" data-id="element-4281" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-purple-400" data-id="element-4282">
                    LIFETIME UPDATES
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-4283">
                  <p className="text-stone-100" data-id="element-4284">
                    The course has already been updated multiple times since its
                    original release.
                  </p>

                  <p className="text-lg sm:text-xl text-purple-400 font-bold" data-id="element-4285">
                    It's now 10x bigger than when it launched.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-4286">
                    And you get access to EVERY future update, forever.
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-purple-500/30 mt-6 text-sm sm:text-base" data-id="element-4287">
                    <p data-id="element-4288">As new research emerges...</p>
                    <p data-id="element-4289">
                      As Brenden refines protocols based on new client
                      results...
                    </p>
                    <p data-id="element-4290">
                      As the science advances and new peptides become
                      available...
                    </p>
                  </div>

                  <p className="text-xl sm:text-2xl text-center text-purple-400 font-bold mt-6" data-id="element-4291">
                    You stay at the forefront.
                  </p>
                </div>
              </div>
            </div>

            {/* 12-MONTH TRANSFORMATION JOURNEY */}
            <div className="mb-8 sm:mb-12" data-id="element-4292">
              <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-6 sm:p-8 rounded-lg" data-id="element-4293">
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-4294">
                  <Award className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400" data-id="element-4295" />
                  <h4 className="text-2xl sm:text-3xl font-bold text-amber-400" data-id="element-4296">
                    STRUCTURED AS A 12-MONTH TRANSFORMATION JOURNEY
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-4297">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-4298">
                    Peptide Mastery 2.0 is strategically sequenced as a 12-month
                    progression that takes you from foundational understanding
                    to complete mastery.
                  </p>

                  <p className="text-lg sm:text-xl text-amber-400 font-bold mb-4" data-id="element-4299">
                    Each month unlocks a new phase:
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4300">
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4301">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4302">Month 1:</p>
                      <p className="text-sm" data-id="element-4303">
                        Introduction & Basics + Mass Spectrometry Analysis
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4304">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4305">Month 2:</p>
                      <p className="text-sm" data-id="element-4306">
                        Sleep Optimization, Circadian Rhythm Repair & Stress
                        Adaptation
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4307">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4308">Month 3:</p>
                      <p className="text-sm" data-id="element-4309">
                        Gut Health, Digestive Repair & Detoxification
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4310">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4311">Month 4:</p>
                      <p className="text-sm" data-id="element-4312">
                        Brain Health, Cognitive Enhancement & Neuro-Regeneration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4313">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4314">Month 5:</p>
                      <p className="text-sm" data-id="element-4315">
                        Cardiovascular Regeneration, Metabolic Health & Thyroid
                        Function
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4316">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4317">Month 6:</p>
                      <p className="text-sm" data-id="element-4318">
                        Joint, Bone & Cartilage Regeneration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4319">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4320">Month 7:</p>
                      <p className="text-sm" data-id="element-4321">
                        Energy Production, Sports Performance & Endurance
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4322">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4323">Month 8:</p>
                      <p className="text-sm" data-id="element-4324">
                        Men's Hormonal Health, Vitality & Fertility
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4325">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4326">Month 9:</p>
                      <p className="text-sm" data-id="element-4327">
                        Women's Hormonal Balance, Thyroid & Reproductive Health
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4328">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4329">Month 10:</p>
                      <p className="text-sm" data-id="element-4330">
                        Injury Repair & Vision Restoration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4331">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4332">Month 11:</p>
                      <p className="text-sm" data-id="element-4333">
                        Total Immune Defense, Respiratory Health & Infection
                        Control
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-4334">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-4335">Month 12:</p>
                      <p className="text-sm" data-id="element-4336">
                        Ultimate Longevity, Skin Rejuvenation & Detoxification
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-4337">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-4338">
                      This systematic approach ensures:
                    </p>
                    <div className="space-y-2 pl-4 text-sm sm:text-base" data-id="element-4339">
                      <p className="flex items-start gap-3" data-id="element-4340">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-4341">→</span>
                        <span data-id="element-4342">You're not overwhelmed with information</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-4343">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-4344">→</span>
                        <span data-id="element-4345">
                          You build comprehensive understanding methodically
                        </span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-4346">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-4347">→</span>
                        <span data-id="element-4348">
                          You can implement protocols as you learn them
                        </span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-4349">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-4350">→</span>
                        <span data-id="element-4351">
                          You develop true mastery rather than surface knowledge
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="text-center mt-8" data-id="element-4352">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-4353">
                      By the end of 12 months, you won't just have random
                      knowledge.
                    </p>
                    <p className="text-xl sm:text-2xl text-amber-400 font-bold" data-id="element-4354">
                      You'll have systematic mastery of biological optimization.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Final Summary */}
            <div className="text-center space-y-4 sm:space-y-6 mt-8 sm:mt-12" data-id="element-4355">
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-100" data-id="element-4356">
                Again, that's{' '}
                <span className="text-amber-400" data-id="element-4357">Peptide Mastery 2.0</span>.
              </p>
              <p className="text-lg sm:text-xl text-stone-300" data-id="element-4358">
                Normally{' '}
                <span className="text-red-400 font-bold line-through" data-id="element-4359">
                  $2,500
                </span>{' '}
                (pay-in-full only).
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-green-400" data-id="element-4360">
                But when you join PSI PRO, you get it INCLUDED FREE.
              </p>
            </div>
          </div>
        </div>

        {/* CTA after value reveal */}
        <div className="text-center mt-10 sm:mt-14" data-id="element-4361">
          <button onClick={() => {
          const pricingSection = document.querySelector('[data-pricing-section]');
          if (pricingSection) {
            pricingSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 min-h-[44px]" data-id="element-4362">
            Join the Private Research Order Now
          </button>
        </div>

        {/* Transition to next section */}
        <div className="text-center mt-12 sm:mt-16" data-id="element-4363">
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-100 mb-4" data-id="element-4364">
            Now here's where this gets truly{' '}
            <span className="text-purple-400" data-id="element-4365">unprecedented</span>...
          </p>
        </div>
      </div>
    </section>;
}